<template>
    <div>
        <!-- Asset Symbol Filter -->
        <rounded-container variant="dark" class="mb-3" v-if="staleTotalCount > 0">
            <b-row class="px-3 pt-3">
                <b-col cols="4">Filter:</b-col>
                <b-col>
                    <b-form-select
                        v-model="selectedSymbol"
                        :options="symbolOptions"
                        :disabled="bulkRunning"
                        size="sm"
                    ></b-form-select>
                </b-col>
            </b-row>

            <b-row class="px-3 pb-3">
                <b-col cols="12" class="text-center">
                    <small class="text-muted mt-2 d-block">
                        {{ pageDescription }}
                    </small>
                </b-col>
            </b-row>
        </rounded-container>

        <div v-if="canBulkReinvest" class="text-center bulk-action">
            <b-button
                variant="success"
                size="sm"
                :disabled="bulkRunning"
                @click="confirmVisible = true"
            >
                <b-spinner v-if="bulkRunning" small class="mr-1"></b-spinner>
                Reinvest all {{ stalePositions.length }} {{ selectedSymbol }} positions
                (${{ formatNumber(pageValue) }})
            </b-button>
        </div>

        <confirm-prompt
            :visible="confirmVisible"
            :title="`Reinvest ${selectedSymbol}`"
            :message="confirmMessage"
            :confirm-label="`Reinvest ${stalePositions.length}`"
            confirm-variant="btn-success"
            @confirm="processBulkReinvest"
            @cancel="confirmVisible = false"
        />

        <rounded-container v-if="loading" variant="dark">
            <template v-slot:title>Stale Positions</template>

            <div class="text-center p-3">
                <b-spinner small type="grow" variant="success"></b-spinner>
            </div>
        </rounded-container>
        <rounded-container v-else-if="!gotData" variant="dark">
            <template v-slot:title>Stale Positions</template>

            <div class="text-center p-3">
                Could not load stale positions.
            </div>
        </rounded-container>
        <rounded-container v-else-if="noStalePositionsFound" variant="dark">
            <template v-slot:title>Stale Positions</template>

            <div class="text-center p-3">
                No stale positions found.
            </div>
        </rounded-container>
        <rounded-container v-else>
            <template v-slot:title>Stale Positions</template>

            <stale-positions-table
                :positions="stalePositions"
                :active-buttons="activeButtons"
                @reinvest="processReinvest"
            />
        </rounded-container>



        <rounded-container variant="dark" v-if="hasRecentReinvestments">
            <template v-slot:title>
                Reinvested in the past {{ closedStats.days }} days
                <span class="text-secondary">${{ formatNumber(closedStats.total_value) }}</span>
            </template>

            <div class="oddRow p-3">
                <span v-for="assetSummary in closedStats.by_symbol" :key="assetSummary.ticker" class="mr-3 text-secondary">
                    <strong>{{ assetSummary.position_count }}x</strong>&nbsp;{{ assetSummary.ticker }}&nbsp;${{ formatNumber(assetSummary.value) }}
                </span>
            </div>

            <div v-if="staleClosed.length">
                <div v-if="staleClosedLimit && staleClosed.length >= staleClosedLimit" class="px-3 pt-3 text-muted">
                    <small>{{ staleClosedLimit }} most recent</small>
                </div>

                <div v-for="(position, rowIndex) in staleClosed"
                    :key="position.pid"
                    :class="rowClass(rowIndex)"
                    class="p-3"
                    >
                    <b-row no-gutters class="pt-1">
                        <b-col cols=3>
                            <asset-symbol :asset="getAssetBySymbol(position.ticker)" />
                        </b-col>
                        <b-col cols=3>
                            <span class="formatPrice">${{ formatPrice(position.current_price) }}</span>
                        </b-col>
                        <b-col cols=3>
                            <span class="formatPrice">${{ position.open_price }}</span> &#x21E2;
                        </b-col>
                        <b-col cols=3>
                            <span class="formatPrice">${{ position.close_price }}</span>
                        </b-col>
                    </b-row>
                    <b-row no-gutters class="pb-3">
                        <b-col cols=3>
                            <span v-if="position.size !== defaultPositionSize" class="text-danger">
                                ${{ position.size }}
                            </span>
                        </b-col>
                        <b-col cols=3>
                            <b-badge v-if="position.indicator > 0" variant="dark">{{ position.indicator }}%</b-badge>
                            <b-badge v-else variant="warning">{{ position.indicator }}%</b-badge>
                        </b-col>
                        <b-col cols=3 class="text-secondary">
                            {{ position.open_date }}
                        </b-col>
                        <b-col cols=3 class="text-secondary">
                            {{ position.close_date }}
                        </b-col>
                    </b-row>
                </div>
            </div>
        </rounded-container>

    </div>
</template>


<script>

import { mapState } from 'vuex';

import apiMixin from '../mixins/api.js';
import formatterMixin from '../mixins/formatter.js';
import RoundedContainer from '../components/style/RoundedContainer.vue';
import AssetSymbol from '../components/style/AssetSymbol.vue';
import StalePositionsTable from '../components/asset/StalePositionsTable.vue';
import ConfirmPrompt from '../components/overlays/ConfirmPrompt.vue';

const EMPTY_CLOSED_STATS = { days: null, position_count: 0, total_value: 0, by_symbol: [] };

export default {
    name: 'StalePositions',
    mixins: [apiMixin, formatterMixin],
    components: {
        RoundedContainer,
        AssetSymbol,
        StalePositionsTable,
        ConfirmPrompt
    },

    data() {
        return {
            gotData: false,
            loading: true,
            requestSeq: 0,
            activeButtons: [],
            stalePositions: [],
            staleTotalCount: 0,
            staleSymbolCounts: {},
            staleClosed: [],
            staleClosedLimit: null,
            closedStats: { ...EMPTY_CLOSED_STATS },
            selectedSymbol: '',
            bulkRunning: false,
            confirmVisible: false,
        }
    },
    methods: {
        getAssetBySymbol(symbol) {
            return this.$store.getters.getAssetBySymbol(symbol);
        },
        async processReinvest(pid) {
            const index = this.activeButtons.indexOf(pid);
            if (index > -1) {
                this.activeButtons.splice(index, 1);
            }

            const response = await this.apiPost('/api/reinvest_position', { pid });

            if (!response) {
                // Re-add button on failure
                this.activeButtons.push(pid);
            }

            this.retrieveStalePositions();
        },
        async processBulkReinvest() {
            this.confirmVisible = false;

            const symbol = this.selectedSymbol;
            const pids = this.stalePositions.map(position => position.pid);
            if (!symbol || !pids.length) return;

            this.bulkRunning = true;
            this.activeButtons = [];

            await this.apiPost('/api/positions/stale/bulk_reinvest', { symbol, pids });
            this.bulkRunning = false;

            this.retrieveStalePositions();
        },
        async retrieveStalePositions() {
            const seq = ++this.requestSeq;
            this.loading = true;

            const requestedSymbol = this.selectedSymbol;
            const response = await this.apiGet('/api/positions/stale', requestedSymbol ? { symbol: requestedSymbol } : null);

            // a slower response for a filter we've since moved off must not overwrite the page
            if (seq !== this.requestSeq) return;

            this.loading = false;
            if (!response) return;
            if ((response.data.symbol || '') !== requestedSymbol) return;

            this.stalePositions = response.data.stale_positions;
            this.staleTotalCount = response.data.stale_total_count;
            this.staleSymbolCounts = response.data.stale_symbol_counts;
            this.staleClosed = response.data.stale_closed;
            this.staleClosedLimit = response.data.stale_closed_limit;
            this.closedStats = response.data.stale_closed_stats || { ...EMPTY_CLOSED_STATS };

            this.activeButtons = this.stalePositions.map(position => position.pid);

            this.gotData = true;

            // the filtered asset just ran out of stale positions — drop back to all symbols
            if (this.selectedSymbol && !this.staleSymbolCounts[this.selectedSymbol]) {
                this.selectedSymbol = '';
            }
        },
    },
    created() {
        this.retrieveStalePositions();
    },
    watch: {
        selectedSymbol() {
            this.retrieveStalePositions();
        }
    },
    computed: {
        ...mapState({
            defaultPositionSize: state => state.positions.defaultPositionSize,
        }),
        noStalePositionsFound() {
            return !this.stalePositions.length;
        },
        symbolOptions() {
            const symbols = Object.keys(this.staleSymbolCounts).sort();

            return [
                { value: '', text: `All symbols (${this.staleTotalCount})` },
                ...symbols.map(symbol => ({
                    value: symbol,
                    text: `${symbol} (${this.staleSymbolCounts[symbol]})`
                }))
            ];
        },
        pageValue() {
            return this.stalePositions.reduce((total, position) => total + position.size, 0);
        },
        pageDescription() {
            if (this.selectedSymbol) {
                return `Showing all ${this.stalePositions.length} ${this.selectedSymbol} positions`;
            }

            if (this.stalePositions.length < this.staleTotalCount) {
                return `Showing the oldest ${this.stalePositions.length} of ${this.staleTotalCount} stale positions`;
            }
            return `Showing all ${this.stalePositions.length} stale positions`;
        },
        confirmMessage() {
            return `Close ${this.stalePositions.length} ${this.selectedSymbol} positions`
                + ` ($${this.formatNumber(this.pageValue)}) and open a replacement for each?`;
        },
        hasRecentReinvestments() {
            return this.closedStats.position_count > 0;
        },
        canBulkReinvest() {
            return !this.loading && this.selectedSymbol && this.stalePositions.length > 0;
        },
    }
}

</script>


<style scoped>
/* 2rem, double the mb-3 the surrounding containers use */
.bulk-action {
    margin: 2rem 0;
}
</style>
